import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  type ColumnDef,
  DataTable,
  EmptyState,
  ErrorState,
  Pagination,
  Spinner,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  TextLink,
} from '@olytic/foundation';
import { Send } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { formatDate } from '../objects/format';
import { recordPath } from '../objects/routes';
import {
  listPartnerReferrals,
  type PartnerReferral,
  type PartnerReferralList,
  REFERRAL_STAGES,
  type ReferralCounts,
  type ReferralStage,
  REFERRALS_PAGE_SIZE,
} from './partnersApi';

/**
 * The Referrals section of a partner's page (MOTHER-123): the companies a partner has
 * sent, where each stands, and the lead and opportunity each became, from MOTHER-122's
 * route. Read-only. A failure here leaves the rest of the page as it is.
 */

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** A tab: a stage, or every referral. */
export type ReferralsTab = 'all' | ReferralStage;

/** The tabs, in order (PM decision 1); the section opens on All. */
export const REFERRAL_TABS: readonly { key: ReferralsTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'in_discussions', label: 'In Discussions' },
  { key: 'closed_won', label: 'Closed Won' },
  { key: 'closed_lost', label: 'Closed Lost' },
  { key: 'not_a_fit', label: 'Not a Fit' },
];

/** The address parameter that holds the tab, beside the page's own: `?stage=contacted`. All has none. */
export const STAGE_PARAM = 'stage';

const STAGE_TONE: Record<ReferralStage, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  submitted: 'neutral',
  contacted: 'info',
  in_discussions: 'warning',
  closed_won: 'success',
  closed_lost: 'error',
  not_a_fit: 'neutral',
};

const isStage = (value: string | null): value is ReferralStage => (REFERRAL_STAGES as readonly string[]).includes(value ?? '');
const stageLabel = (stage: string): string => REFERRAL_TABS.find((t) => t.key === stage)?.label ?? stage;
const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');
const contactName = (r: PartnerReferral): string => [r.firstName, r.lastName].filter((part) => part.trim() !== '').join(' ');

/** The tab an address asks for; All when it names no stage, or one this page does not know. */
export function readStageParam(params: URLSearchParams): ReferralsTab {
  const stage = params.get(STAGE_PARAM);
  return isStage(stage) ? stage : 'all';
}

/** A tab's count once the server has sent the counts. `All` is the sum of the six. */
export function referralTabCount(tab: ReferralsTab, counts: ReferralCounts | null): number | null {
  if (counts === null) return null;
  return tab === 'all' ? REFERRAL_STAGES.reduce((sum, s) => sum + counts[s], 0) : counts[tab];
}

/** The line above the table (PM decision 3): `7 referrals · 2 won · 3 in discussions`. */
export function referralSummary(counts: ReferralCounts): string {
  const all = referralTabCount('all', counts) ?? 0;
  return `${all} ${all === 1 ? 'referral' : 'referrals'} · ${counts.closed_won} won · ${counts.in_discussions} in discussions`;
}

function StageBadge({ stage }: { stage: string }): JSX.Element {
  return (
    <Badge tone={isStage(stage) ? STAGE_TONE[stage] : 'neutral'} size="sm">
      {stageLabel(stage)}
    </Badge>
  );
}

/** The six columns, in order (PM decision 2). Company links to the opportunity only when there is one. */
const COLUMNS: ColumnDef<PartnerReferral>[] = [
  {
    key: 'companyName',
    label: 'Company',
    primary: true,
    get: (row) => row.companyName,
    href: (row) => (row.opportunityId === null ? null : recordPath('opportunities', row.opportunityId)),
  },
  { key: 'contact', label: 'Contact', get: contactName },
  { key: 'stage', label: 'Stage', mobileHeader: true, get: (row) => row.stage, render: (row) => <StageBadge stage={row.stage} /> },
  { key: 'stageChangedAt', label: 'Stage Changed', nowrap: true, get: (row) => row.stageChangedAt, render: (row) => formatDate(row.stageChangedAt) },
  { key: 'createdAt', label: 'Submitted', nowrap: true, get: (row) => row.createdAt, render: (row) => formatDate(row.createdAt) },
  {
    key: 'lead',
    label: 'Lead',
    get: (row) => row.leadId,
    render: (row) =>
      row.leadId === null ? (
        ''
      ) : (
        <TextLink asChild>
          <Link to={recordPath('leads', row.leadId)}>Open Lead</Link>
        </TextLink>
      ),
  },
];

type Load = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; list: PartnerReferralList };

/** Partners whose application is not verified have sent nothing: every tab reads as none yet (PM decision 4). */
const NOT_REFERRING: readonly string[] = ['pending', 'declined'];

/** The empty text: none yet on All, or for a partner who cannot refer; none at this stage otherwise. */
function referralsEmptyText(tab: ReferralsTab, partnerStatus: string | null): string {
  return tab === 'all' || (partnerStatus !== null && NOT_REFERRING.includes(partnerStatus)) ? 'No referrals yet.' : 'No referrals at this stage.';
}

export function PartnerReferrals({
  fetcher,
  partnerId,
  partnerStatus,
}: {
  fetcher: Fetch;
  partnerId: string;
  /** The partner's status once the page has it; null while it loads. */
  partnerStatus: string | null;
}): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const tab = readStageParam(params);
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [counts, setCounts] = useState<ReferralCounts | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The page's offset belongs to one tab. Any change of tab, by a click or the address,
  // starts again at the first page, including on coming back to a tab paged before.
  const [paging, setPaging] = useState({ tab, offset: 0 });
  if (paging.tab !== tab) setPaging({ tab, offset: 0 });
  const offset = paging.tab === tab ? paging.offset : 0;

  useEffect(() => {
    let live = true;
    setLoad({ state: 'loading' });
    listPartnerReferrals(fetcher, partnerId, { stage: tab === 'all' ? '' : tab, limit: REFERRALS_PAGE_SIZE, offset }).then(
      (list) => {
        if (!live) return;
        setLoad({ state: 'ready', list });
        setCounts(list.counts);
      },
      (error: unknown) => live && setLoad({ state: 'error', message: errorText(error) }),
    );
    return () => {
      live = false;
    };
  }, [fetcher, partnerId, tab, offset, attempt]);

  // Another tab keeps the page's other parameters and its history state (the back link's list).
  const choose = (next: ReferralsTab) => {
    const search = new URLSearchParams(params);
    if (next === 'all') search.delete(STAGE_PARAM);
    else search.set(STAGE_PARAM, next);
    setParams(search, { state: location.state });
  };

  const tabs = useMemo(
    () =>
      REFERRAL_TABS.map((t) => {
        const n = referralTabCount(t.key, counts);
        return (
          <Tab key={t.key} value={t.key}>
            {t.label}
            {n === null ? null : (
              <>
                {' '}
                <Badge size="sm" variant="soft" tone="neutral">
                  {n}
                </Badge>
              </>
            )}
          </Tab>
        );
      }),
    [counts],
  );

  const list = load.state === 'ready' ? load.list : null;
  const empty = referralsEmptyText(tab, partnerStatus);

  return (
    <Card>
      <CardHeader title="Referrals" icon={<Send />} headingLevel={2} />
      <CardBody>
        <Stack gap="sm">
          {counts === null ? null : (
            <Text tone="muted" size="sm">
              {referralSummary(counts)}
            </Text>
          )}
          <Tabs value={tab} onValueChange={(value) => (value === 'all' || isStage(value)) && choose(value)}>
            {/* Seven tabs and their counts are wider than a phone: they wrap rather than scroll. */}
            <TabList aria-label="Referral Stage" className="flex-wrap">
              {tabs}
            </TabList>
          </Tabs>
          {load.state === 'loading' ? <Spinner label="Loading referrals…" /> : null}
          {load.state === 'error' ? <ErrorState title="Could not load referrals" description={load.message} onRetry={() => setAttempt((n) => n + 1)} /> : null}
          {list ? (
            <>
              <DataTable<PartnerReferral>
                aria-label="Referrals"
                columns={COLUMNS}
                rows={list.referrals}
                getRowId={(row) => row.id}
                mode="server"
                fit
                onNavigate={(href) => navigate(href)}
                empty={<EmptyState icon={<Send />} title={empty} />}
              />
              <Pagination
                mode="pages"
                total={list.total}
                page={Math.floor(offset / REFERRALS_PAGE_SIZE) + 1}
                pageSize={REFERRALS_PAGE_SIZE}
                onPageChange={(page) => setPaging({ tab, offset: (page - 1) * REFERRALS_PAGE_SIZE })}
              />
            </>
          ) : null}
        </Stack>
      </CardBody>
    </Card>
  );
}
